import {payinAuditLabel} from "../../utils/payinAudit";
import {ExternalCompletionBadge, ExternalCompletionDetails} from "../../components/ExternalCompletion";
import React, {useEffect, useState} from "react";
import {cancelStalledOrder, closeExchangePayout, downloadReceipt, getOrder, getOrderTimeline, getTraderHistory, listExchangePayoutObserved, listOrders} from "../../api";
import type {ExchangePayoutObservedItem, OrderDetailsResponse, OrderItem, OrderSide, OrderStatus} from "../../api";
import {displayedOrderStatus, exchangePayoutOpen, observedVerdictLabel} from "./adminOrders";
import {useAuth} from "../../auth";
import {AttemptReceipts, ErrorBanner, Modal} from "../../components";
import {navigate} from "../../hooks";
import type {AuditEventItem, HistoryAttempt, HistoryReceipt} from "../../types";
import {formatAmount, formatDate, maskCard, statusClass} from "../../utils";

const ORDERS_LIMIT = 50;
const ORDER_STATUSES: OrderStatus[] = ["new", "pending", "partially_filled", "success", "cancelled", "appeal"];
const ORDER_SIDES: OrderSide[] = ["out", "in"];

interface OrderFilters {
    remoteId: string;
    status: "" | OrderStatus;
    merchantName: string;
    side: "" | OrderSide;
    from: string;
    to: string;
}

const emptyFilters: OrderFilters = {
    remoteId: "",
    status: "",
    merchantName: "",
    side: "",
    from: "",
    to: "",
};

export function AdminOrdersPage(): JSX.Element {
    const {token} = useAuth();

    const [draft, setDraft] = useState<OrderFilters>(emptyFilters);
    const [applied, setApplied] = useState<OrderFilters>(emptyFilters);
    const [offset, setOffset] = useState(0);
    const [reloadKey, setReloadKey] = useState(0);
    const [items, setItems] = useState<OrderItem[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [nextOffset, setNextOffset] = useState<number | null>(null);
    const [selected, setSelected] = useState<OrderItem | null>(null);
    const [pendingCancel, setPendingCancel] = useState<OrderItem | null>(null);
    const [cancelling, setCancelling] = useState(false);
    const [cancelError, setCancelError] = useState<unknown>(null);
    // Ручное закрытие USDT-этапа. Своё состояние и своя модалка: у отмены
    // заявки предмет другой — там заявка умирает, здесь закрывается вторая
    // нога уже успешной сделки, и деньги не двигаются ни там, ни здесь.
    const [pendingPayoutClose, setPendingPayoutClose] = useState<OrderItem | null>(null);
    const [payoutTxHash, setPayoutTxHash] = useState("");
    const [payoutReason, setPayoutReason] = useState("");
    const [closingPayout, setClosingPayout] = useState(false);
    const [payoutError, setPayoutError] = useState<unknown>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<unknown>(null);

    useEffect(() => {
        let cancelled = false;
        if (!token) {
            setItems([]);
            setHasMore(false);
            setNextOffset(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        listOrders(token, {
            remoteId: applied.remoteId || undefined,
            merchantName: applied.merchantName || undefined,
            status: applied.status || undefined,
            side: applied.side || undefined,
            from: applied.from || undefined,
            to: applied.to || undefined,
            limit: ORDERS_LIMIT,
            offset,
        })
            .then((resp) => {
                if (cancelled) return;
                setItems(resp.items ?? []);
                setHasMore(resp.hasMore);
                setNextOffset(resp.nextOffset ?? null);
            })
            .catch((err) => {
                if (!cancelled) setError(err);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [
        applied.from,
        applied.merchantName,
        applied.remoteId,
        applied.side,
        applied.status,
        applied.to,
        offset,
        reloadKey,
        token,
    ]);

    const submitFilters = (event?: React.FormEvent): void => {
        event?.preventDefault();
        setApplied({...draft});
        setOffset(0);
        setReloadKey((value) => value + 1);
    };

    const clearFilters = (): void => {
        setDraft(emptyFilters);
        setApplied(emptyFilters);
        setOffset(0);
        setReloadKey((value) => value + 1);
    };

    // Отмена вставшей заявки кабинета. Через подтверждение, как и решения по
    // спорам: заявка закрывается насовсем, и промахнуться мимо строки в таблице
    // на полсотни строк слишком легко.
    const confirmCancel = async (): Promise<void> => {
        if (!pendingCancel || !token) return;
        setCancelling(true);
        setCancelError(null);
        try {
            await cancelStalledOrder(token, pendingCancel.orderId);
            setPendingCancel(null);
            setReloadKey((value) => value + 1);
        } catch (err) {
            setCancelError(err);
        } finally {
            setCancelling(false);
        }
    };

    // Закрытие USDT-этапа за пропавшего трейдера. Причина обязательна: этап
    // закрывает не тот, кто отправлял деньги, и разбор через месяц обязан
    // прочитать словами, почему так вышло. Hash — по желанию: админ может его
    // не знать вовсе.
    const confirmPayoutClose = async (): Promise<void> => {
        if (!pendingPayoutClose || !token) return;
        setClosingPayout(true);
        setPayoutError(null);
        try {
            await closeExchangePayout(token, pendingPayoutClose.orderId, {
                txHash: payoutTxHash.trim() === "" ? null : payoutTxHash.trim(),
                reason: payoutReason.trim(),
            });
            setPendingPayoutClose(null);
            setPayoutTxHash("");
            setPayoutReason("");
            setReloadKey((value) => value + 1);
        } catch (err) {
            setPayoutError(err);
        } finally {
            setClosingPayout(false);
        }
    };

    if (selected) {
        return <AdminOrderDetails item={selected} onBack={() => setSelected(null)}/>;
    }

    const showingFrom = items.length === 0 ? 0 : offset + 1;
    const showingTo = offset + items.length;

    return (
        <div className="page admin-orders">
            <div className="page-header">
                <h2>Orders</h2>
                <button className="btn btn-ghost btn-sm" type="button" onClick={() => submitFilters()} disabled={loading}>
                    {loading ? "Refreshing..." : "Refresh"}
                </button>
            </div>

            <form className="history-toolbar activity-toolbar admin-orders-toolbar" onSubmit={submitFilters}>
                <div className="history-filter-groups">
                    <label className="activity-date-filter activity-search-filter">
                        <span>remoteId</span>
                        <input
                            type="search"
                            value={draft.remoteId}
                            placeholder="remote id"
                            onChange={(event) => setDraft((prev) => ({...prev, remoteId: event.target.value}))}
                        />
                    </label>
                    <label className="activity-date-filter">
                        <span>Status</span>
                        <select
                            value={draft.status}
                            onChange={(event) => setDraft((prev) => ({...prev, status: event.target.value as OrderFilters["status"]}))}
                        >
                            <option value="">Any</option>
                            {ORDER_STATUSES.map((status) => (
                                <option key={status} value={status}>{status}</option>
                            ))}
                        </select>
                    </label>
                    <label className="activity-date-filter">
                        <span>Merchant</span>
                        <input
                            type="search"
                            value={draft.merchantName}
                            placeholder="name"
                            onChange={(event) => setDraft((prev) => ({...prev, merchantName: event.target.value}))}
                        />
                    </label>
                    <label className="activity-date-filter">
                        <span>Side</span>
                        <select
                            value={draft.side}
                            onChange={(event) => setDraft((prev) => ({...prev, side: event.target.value as OrderFilters["side"]}))}
                        >
                            <option value="">Any</option>
                            {ORDER_SIDES.map((side) => (
                                <option key={side} value={side}>{side}</option>
                            ))}
                        </select>
                    </label>
                    <label className="activity-date-filter">
                        <span>From</span>
                        <input
                            type="date"
                            value={draft.from}
                            onChange={(event) => setDraft((prev) => ({...prev, from: event.target.value}))}
                        />
                    </label>
                    <label className="activity-date-filter">
                        <span>To</span>
                        <input
                            type="date"
                            value={draft.to}
                            onChange={(event) => setDraft((prev) => ({...prev, to: event.target.value}))}
                        />
                    </label>
                </div>
                <div className="admin-orders-filter-actions">
                    <button className="btn btn-primary btn-sm" type="submit" disabled={loading}>Search</button>
                    <button className="btn btn-ghost btn-sm" type="button" onClick={clearFilters} disabled={loading}>Clear</button>
                </div>
            </form>

            <ErrorBanner error={error} title="Could not load orders"/>

            <section className="history-list-card activity-card">
                <div className="history-list-header">
                    <strong>Order pool</strong>
                    <span className="badge">{loading && items.length === 0 ? "..." : items.length}</span>
                </div>

                {items.length === 0 ? (
                    <div className="empty-state compact">
                        {loading ? "Loading orders..." : "No orders found"}
                    </div>
                ) : (
                    <div className="activity-table-wrap admin-orders-table-wrap">
                        <table className="activity-table">
                            <thead>
                                <tr>
                                    <th>status</th>
                                    <th>remoteId</th>
                                    <th>merchant</th>
                                    <th>side</th>
                                    <th>amount</th>
                                    <th>remaining</th>
                                    <th>parts</th>
                                    <th>created</th>
                                    <th/>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item) => {
                                    const displayedStatus = displayedOrderStatus(item.status, item.appeal);
                                    return (
                                    <tr key={item.orderId} className="admin-order-row" onClick={() => setSelected(item)}>
                                        <td>
                                            <span className={`payment-status ${statusClass(displayedStatus)}`}>
                                                {displayedStatus}
                                            </span>
                                            {/* Заявка кабинета, которую уже нельзя провести, а трейдер её
                                                не брал: спора по ней не существует, из пула она ушла — и
                                                без этой метки админ не увидел бы её нигде.

                                                Причин ровно две, и обе окончательные (В-29): площадка
                                                закрыла карточку либо на заявке замок автоматики. Прошедший
                                                срок площадки и «придержана» сюда больше не попадают — такая
                                                заявка живая, лежит в пуле и берётся. */}
                                            {item.externalStalled && (
                                                <span
                                                    className="payment-status status-stalled"
                                                    title="Заявка кабинета встала: площадка закрыла карточку или на заявке замок автоматики. Трейдер её не брал, поэтому спора нет — закрывать отменой."
                                                >
                                                    встала
                                                </span>
                                            )}
                                            {/* Заявка-обмен с незакрытой второй ногой: рубли
                                                проведены, USDT клиенту ещё не отправлены.
                                                Метка стоит там же, где «встала», и по тому же
                                                правилу — она единственное место, где админ
                                                увидит зависший этап. */}
                                            {exchangePayoutOpen(item) && (
                                                <span
                                                    className="payment-status status-stalled"
                                                    title="USDT-этап заявки-обмена не закрыт: рубли проведены, перевод клиенту не подтверждён."
                                                >
                                                    USDT-этап
                                                </span>
                                            )}
                                        </td>
                                        <td className="mono admin-order-remote-cell">{item.remoteId}<ExternalCompletionBadge completion={item.externalCompletion}/></td>
                                        <td>{item.merchantName}</td>
                                        <td>
                                            <span className={`details-dir-badge ${item.side}`}>{item.side === "in" ? "Входящая" : "Исходящая"}</span>
                                        </td>
                                        <td className="mono">{formatAmount(item.amount)} RUB</td>
                                        <td className="mono">{formatAmount(item.amountRemaining)} RUB</td>
                                        <td>
                                            <span className="dispute-parts">{item.paymentsMade}/{item.maxPayments}</span>
                                        </td>
                                        <td className="mono">{formatDate(item.createdAt)}</td>
                                        <td className="merchant-action-cell">
                                            {/* Отмена — единственное, что админ может сделать со вставшей
                                                заявкой: спора по ней нет, потому что нет платежа. Кнопка
                                                стоит ровно там же, где метка, и по тому же признаку. */}
                                            {item.externalStalled && (
                                                <button
                                                    className="btn btn-danger btn-sm"
                                                    type="button"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        setCancelError(null);
                                                        setPendingCancel(item);
                                                    }}
                                                >
                                                    Отменить
                                                </button>
                                            )}
                                            {/* Запасной путь на случай пропавшего трейдера.
                                                Денег не двигает: закрывается вторая нога уже
                                                успешной сделки. */}
                                            {exchangePayoutOpen(item) && (
                                                <button
                                                    className="btn btn-ghost btn-sm"
                                                    type="button"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        setPayoutError(null);
                                                        setPayoutTxHash("");
                                                        setPayoutReason("");
                                                        setPendingPayoutClose(item);
                                                    }}
                                                >
                                                    Закрыть USDT-этап
                                                </button>
                                            )}
                                            <button className="btn btn-ghost btn-sm" type="button">Open</button>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="activity-pagination">
                    <span className="mono">{showingFrom}-{showingTo}</span>
                    <div>
                        <button
                            className="btn btn-ghost btn-sm"
                            type="button"
                            disabled={loading || offset === 0}
                            onClick={() => setOffset(Math.max(0, offset - ORDERS_LIMIT))}
                        >
                            Back
                        </button>
                        <button
                            className="btn btn-ghost btn-sm"
                            type="button"
                            disabled={loading || !hasMore}
                            onClick={() => setOffset(nextOffset ?? offset + ORDERS_LIMIT)}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </section>

            <Modal
                open={pendingCancel !== null}
                title="Отменить вставшую заявку"
                onClose={() => {
                    if (!cancelling) setPendingCancel(null);
                }}
                sizeClassName="modal-sm"
                disableClose={cancelling}
            >
                {pendingCancel && (
                    <>
                        <p className="modal-hint">
                            Заявку кабинета больше нельзя провести: площадка закрыла карточку или на заявке
                            инцидентный замок. Трейдер её не брал, поэтому спора по ней не существует.
                            Отмена закрывает заявку у нас; на площадку не уходит ничего.
                        </p>
                        <div className="confirm-summary dispute-confirm-summary">
                            <div className="info-row">
                                <span>remoteId</span>
                                <strong className="mono">{pendingCancel.remoteId}</strong>
                            </div>
                            <div className="info-row">
                                <span>Мерчант</span>
                                <strong>{pendingCancel.merchantName}</strong>
                            </div>
                            <div className="info-row">
                                <span>Сумма</span>
                                <strong className="mono">{formatAmount(pendingCancel.amount)} RUB</strong>
                            </div>
                        </div>
                        <ErrorBanner error={cancelError} />
                        <div className="modal-actions">
                            <button
                                className="btn btn-ghost"
                                type="button"
                                onClick={() => setPendingCancel(null)}
                                disabled={cancelling}
                            >
                                Назад
                            </button>
                            <button
                                className="btn btn-danger"
                                type="button"
                                onClick={confirmCancel}
                                disabled={cancelling}
                            >
                                {cancelling ? "Выполнение..." : "Отменить заявку"}
                            </button>
                        </div>
                    </>
                )}
            </Modal>

            <Modal
                open={pendingPayoutClose !== null}
                title="Закрыть USDT-этап вручную"
                onClose={() => {
                    if (!closingPayout) setPendingPayoutClose(null);
                }}
                sizeClassName="modal-sm"
                disableClose={closingPayout}
            >
                {pendingPayoutClose && (
                    <>
                        <p className="modal-hint">
                            Рубли по заявке уже проведены — это действие денег не двигает. Оно только
                            фиксирует, что USDT ушли клиенту, и толкает мерчанту постбек, чтобы он
                            перечитал статус. Закрывайте только тогда, когда трейдер недоступен, а
                            перевод состоялся.
                        </p>
                        <div className="confirm-summary dispute-confirm-summary">
                            <div className="info-row">
                                <span>remoteId</span>
                                <strong className="mono">{pendingPayoutClose.remoteId}</strong>
                            </div>
                            <div className="info-row">
                                <span>Мерчант</span>
                                <strong>{pendingPayoutClose.merchantName}</strong>
                            </div>
                        </div>
                        <div className="requisites-form">
                            <div className="requisites-grid">
                                <label className="requisites-field">
                                    <span>hash транзакции (если известен)</span>
                                    <input
                                        className="mono"
                                        type="text"
                                        autoComplete="off"
                                        spellCheck={false}
                                        maxLength={64}
                                        placeholder="64 символа: 0-9 и a-f"
                                        value={payoutTxHash}
                                        onChange={(event) => setPayoutTxHash(event.target.value)}
                                        disabled={closingPayout}
                                    />
                                </label>
                                <label className="requisites-field">
                                    <span>Причина *</span>
                                    <input
                                        type="text"
                                        value={payoutReason}
                                        onChange={(event) => setPayoutReason(event.target.value)}
                                        disabled={closingPayout}
                                    />
                                </label>
                            </div>
                        </div>
                        <ErrorBanner error={payoutError} />
                        <div className="modal-actions">
                            <button
                                className="btn btn-ghost"
                                type="button"
                                onClick={() => setPendingPayoutClose(null)}
                                disabled={closingPayout}
                            >
                                Назад
                            </button>
                            <button
                                className="btn btn-success"
                                type="button"
                                onClick={confirmPayoutClose}
                                disabled={closingPayout || payoutReason.trim() === ""}
                            >
                                {closingPayout ? "Выполнение..." : "Закрыть этап"}
                            </button>
                        </div>
                    </>
                )}
            </Modal>
        </div>
    );
}

function AdminOrderDetails({item, onBack}: {item: OrderItem; onBack: () => void}): JSX.Element {
    const {token} = useAuth();
    const [details, setDetails] = useState<OrderDetailsResponse | null>(null);
    const [attempts, setAttempts] = useState<HistoryAttempt[]>([]);
    const [timeline, setTimeline] = useState<AuditEventItem[]>([]);
    // Находки наблюдателя цепочки по адресу клиента. Пустой массив у любой
    // заявки без второй ноги — их карточка не меняется вовсе.
    const [observed, setObserved] = useState<ExchangePayoutObservedItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<unknown>(null);

    useEffect(() => {
        let cancelled = false;
        if (!token) return;

        setLoading(true);
        setError(null);
        Promise.all([
            getOrder(token, item.orderId),
            getTraderHistory(token, {scope: "all", q: item.remoteId, limit: 50, offset: 0, sort: "desc"}),
            getOrderTimeline(token, item.orderId),
        ])
            .then(([orderDetails, history, timelineResp]) => {
                if (cancelled) return;
                setDetails(orderDetails);
                const historyOrder = (history.items ?? []).find((row) => row.orderId === item.orderId);
                setAttempts(historyOrder?.attempts ?? []);
                setTimeline(timelineResp.items ?? []);
            })
            .catch((err) => {
                if (!cancelled) setError(err);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [item.orderId, item.remoteId, token]);

    // Журнал находок наблюдателя цепочки — отдельным запросом и только по
    // заявке-обмену. У остальных заявок второй ноги нет вовсе, и спрашивать по
    // ним пустой журнал значило бы платить лишним запросом на каждой карточке.
    useEffect(() => {
        let cancelled = false;
        if (!token || item.exchangePayoutStatus == null) return;

        listExchangePayoutObserved(token, item.orderId)
            .then((resp) => {
                if (!cancelled) setObserved(resp.items ?? []);
            })
            .catch((err) => {
                if (!cancelled) setError(err);
            });

        return () => {
            cancelled = true;
        };
    }, [item.orderId, item.exchangePayoutStatus, token]);

    const handleReceiptDownload = async (receipt: HistoryReceipt): Promise<void> => {
        if (!receipt.receiptUrl) return;
        try {
            await downloadReceipt(receipt.receiptUrl, token, `${receipt.receiptId}.pdf`);
        } catch (err) {
            setError(err);
        }
    };

    const displayedStatus = displayedOrderStatus((details?.status as OrderStatus | undefined) ?? item.status, item.appeal);

    return (
        <div className="page admin-order-details">
            <div className="page-header">
                <div className="merchant-details-title">
                    <button className="btn btn-ghost btn-sm" type="button" onClick={onBack}>
                        Back to orders
                    </button>
                    <h2>Order {item.remoteId}</h2>
                </div>
                {item.appeal && (
                    <button className="btn btn-appeal btn-sm" type="button" onClick={() => navigate("#/admin/disputes")}>
                        To dispute resolution
                    </button>
                )}
            </div>

            <ErrorBanner error={error} title="Could not load order details"/>

            <section className="merchant-details-card dispute-info-card">
                <div className="trader-meta-grid">
                    <Meta label="Order ID" value={item.orderId} mono/>
                    <Meta label="remoteId" value={item.remoteId} mono/>
                    <Meta label="Merchant" value={item.merchantName}/>
                    <Meta label="Status" value={displayedStatus}/>
                    <Meta label="Направление" value={item.side === "in" ? "Входящая" : "Исходящая"} />
                    <Meta label="Amount" value={`${formatAmount(item.amount)} RUB`} mono/>
                    <Meta label="Remaining" value={`${formatAmount(item.amountRemaining)} RUB`} mono/>
                    <Meta label="Payments" value={`${item.paymentsMade} / ${item.maxPayments}`} mono/>
                    <Meta label="Currency" value={`${item.currency} / scale ${item.currencyScale}`} mono/>
                    <Meta label="Created" value={formatDate(item.createdAt)} mono/>
                    <Meta label="Updated" value={formatDate(item.updatedAt)} mono/>
                </div>
            </section>

            <section className="merchant-details-card admin-order-detail-card">
                <div className="section-header">
                    <div>
                        <h3>Order fields</h3>
                        <span>{loading ? "Loading..." : "GET /v1/order/{orderId}"}</span>
                    </div>
                </div>
                <div className="trader-meta-grid">
                    <Meta label="Bank" value={details?.bank ?? "-"} mono/>
                    {/* `||`, not `??`: an order with no client name carries an empty
                        string, and only the loading case is undefined. */}
                    <Meta label="Client" value={details?.clientFio || "-"} />
                    <Meta label="Card" value={maskCard(details?.clientCard)} mono/>
                    <Meta label="Phone" value={details?.clientPhone ?? "-"} mono/>
                    <Meta label="Client bank" value={details?.clientBank ?? "-"} />
                    <Meta label="Postback" value={details?.postback ?? "-"} mono/>
                    <Meta label="sub1" value={details?.sub1 ?? "-"} mono/>
                    <Meta label="sub2" value={details?.sub2 ?? "-"} mono/>
                    <Meta label="sub3" value={details?.sub3 ?? "-"} mono/>
                </div>
            </section>

            <section className="history-list-card activity-card admin-order-attempts">
                <div className="history-list-header">
                    <strong>Audit timeline</strong>
                    <span className="badge">{loading && timeline.length === 0 ? "..." : timeline.length}</span>
                </div>
                {timeline.length === 0 ? (
                    <div className="empty-state compact">
                        {loading ? "Loading timeline..." : "No audit events for this order"}
                    </div>
                ) : (
                    <div className="activity-table-wrap">
                        <table className="activity-table history-attempts-table">
                            <thead>
                                <tr>
                                    <th>time</th>
                                    <th>event</th>
                                    <th>actor</th>
                                    <th>context</th>
                                </tr>
                            </thead>
                            <tbody>
                                {timeline.map((event) => (
                                    <tr key={event.id}>
                                        <td className="mono">{formatDate(event.occurredAt)}</td>
                                        <td>{payinAuditLabel(event.payload?.action) ?? eventLabel(event.eventType)}</td>
                                        <td>{event.actorLabel || event.traderName || event.actorType}</td>
                                        <td className="mono">{timelineContext(event)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* Журнал находок наблюдателя цепочки. Read-only и только у
                заявки-обмена: он существует ровно затем, чтобы незасчитанный
                перевод разбирался в админке, а не запросом в базу. */}
            {item.exchangePayoutStatus != null && (
                <section className="history-list-card activity-card admin-order-attempts">
                    <div className="history-list-header">
                        <strong>USDT-этап: находки в сети</strong>
                        <span className="badge">{observed.length}</span>
                    </div>
                    {observed.length === 0 ? (
                        <div className="empty-state compact">
                            Наблюдатель ничего не нашёл на адресе клиента
                        </div>
                    ) : (
                        <div className="activity-table-wrap">
                            <table className="activity-table history-attempts-table">
                                <thead>
                                    <tr>
                                        <th>время блока</th>
                                        <th>tx</th>
                                        <th>от кого</th>
                                        <th>сумма</th>
                                        <th>вердикт</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {observed.map((row) => (
                                        <tr key={`${row.txId}:${row.eventIndex}`}>
                                            <td className="mono">{formatDate(row.blockTime)}</td>
                                            <td className="mono admin-order-remote-cell">{row.txId}</td>
                                            <td className="mono">{row.fromAddress}</td>
                                            <td className="mono">{row.amount} USDT</td>
                                            <td>{observedVerdictLabel(row.verdict)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            )}

            {attempts.filter(attempt => attempt.externalCompletion).map(attempt => <ExternalCompletionDetails key={attempt.paymentId} paymentId={attempt.paymentId} completion={attempt.externalCompletion} token={token} canConfirm/>)}
            <section className="history-list-card activity-card admin-order-attempts">
                <div className="history-list-header">
                    <strong>Attempts</strong>
                    <span className="badge">{loading && attempts.length === 0 ? "..." : attempts.length}</span>
                </div>
                {attempts.length === 0 ? (
                    <div className="empty-state compact">
                        {loading ? "Loading attempts..." : "No attempts for this order"}
                    </div>
                ) : (
                    <div className="activity-table-wrap">
                        <table className="activity-table history-attempts-table">
                            <thead>
                                <tr>
                                    <th>claimed</th>
                                    <th>receipt</th>
                                    <th>finished</th>
                                    <th>status</th>
                                    <th>amount</th>
                                    <th>rate</th>
                                    <th>trader</th>
                                </tr>
                            </thead>
                            <tbody>
                                {attempts.map((attempt) => (
                                    <tr key={attempt.paymentId}>
                                        <td className="mono">{formatDate(attempt.claimedAt)}</td>
                                        <td>
                                            <AttemptReceipts attempt={attempt} onDownload={handleReceiptDownload}/>
                                        </td>
                                        <td className="mono">{formatDate(attempt.finishedAt)}</td>
                                        <td>
                                            <span className={`payment-status ${statusClass(attempt.status)}`}>
                                                {attempt.status}
                                            </span>
                                        </td>
                                        <td className="mono">{attempt.amountReal == null ? "-" : `${formatAmount(attempt.amountReal)} RUB`}</td>
                                        <td className="mono">{attempt.currencyRate ?? "-"}</td>
                                        <td>{attempt.traderName || "-"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}

function Meta({label, value, mono = false}: {label: string; value: string; mono?: boolean}): JSX.Element {
    return (
        <div className="meta-card">
            <span className="meta-label">{label}</span>
            <span className={`meta-value ${mono ? "mono" : ""}`.trim()}>{value}</span>
        </div>
    );
}

const EVENT_LABELS: Record<string, string> = {
    order_created: "Order created",
    order_pooled: "Entered pool",
    order_claimed: "Claimed",
    receipt_uploaded: "Receipt uploaded",
    payment_completed: "Paid",
    payment_cancelled: "Cancelled",
    payment_appealed: "Appeal",
    payment_expired: "Expired",
    appeal_resolved: "Appeal resolved",
    order_partially_filled: "Partially filled",
    order_succeeded: "Order succeeded",
    payin_requisites_set: "Реквизиты приёма введены",
};

function eventLabel(type: string): string {
    return EVENT_LABELS[type] ?? type;
}

function payinRequisitesSummary(event: AuditEventItem): string {
    if (event.eventType !== "payin_requisites_set" || !event.payload) return "";
    const bankName = typeof event.payload.bankName === "string" ? event.payload.bankName : "";
    const cardMask = typeof event.payload.cardMask === "string" ? event.payload.cardMask : "";
    const holder = event.payload.cardHolderPresent ? "ФИО указано" : "";
    return [bankName, cardMask, holder].filter(Boolean).join(" ");
}

function timelineContext(event: AuditEventItem): string {
    const status = event.toStatus ? `${event.fromStatus ?? "-"} -> ${event.toStatus}` : "";
    const amount = event.amountReal == null ? "" : `${formatAmount(event.amountReal)} RUB`;
    const rate = event.currencyRate ? `rate ${event.currencyRate}` : "";
    const requisites = payinRequisitesSummary(event);
    return [status, amount, rate, requisites].filter(Boolean).join(" | ") || "-";
}
